import { useState, useCallback } from 'react';
import { ImageIcon, Upload, FolderOpen, Check, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileBrowser } from '@/components/files/FileBrowser';
import { FileUpload } from '@/components/files/FileUpload';
import { UserFileResponse } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { ImageGenerateTab } from './ImageGenerateTab';
import { GeneratedImageResult } from '../hooks';

export interface MediaPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with selected files from the file browser */
  onFilesSelected: (files: UserFileResponse[]) => void;
  /** Called when new files are uploaded via the upload tab */
  onUploadComplete: (files: UserFileResponse[]) => void;
  /** Called when user confirms an AI-generated image */
  onImageGenerated: (result: GeneratedImageResult) => void;
  /** Accepted MIME types string (e.g. "image/jpeg,image/png,video/mp4") */
  acceptedMimeTypes: string;
  /** Max number of files that can still be added */
  remainingSlots: number;
}

const MEDIA_MIME_PREFIXES = ['image/', 'video/'];

function isMediaFile(file: UserFileResponse): boolean {
  if (!file.mimeType) return false;
  return MEDIA_MIME_PREFIXES.some((prefix) => file.mimeType!.startsWith(prefix));
}

function isAcceptedMimeType(mimeType: string | null, acceptedMimeTypes: string): boolean {
  if (!mimeType) return false;
  const accepted = acceptedMimeTypes.split(',').map((t) => t.trim());
  return accepted.some((acceptedType) => {
    if (acceptedType.endsWith('/*')) {
      return mimeType.startsWith(acceptedType.slice(0, -2));
    }
    return mimeType === acceptedType;
  });
}

export function MediaPickerModal({
  open,
  onOpenChange,
  onFilesSelected,
  onUploadComplete,
  onImageGenerated,
  acceptedMimeTypes,
  remainingSlots,
}: MediaPickerModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<UserFileResponse[]>([]);
  const [uploadRefresh, setUploadRefresh] = useState(0);

  const handleImageConfirmed = useCallback(
    (result: GeneratedImageResult) => {
      onImageGenerated(result);
      setUploadRefresh((n) => n + 1);
      toast({ description: 'AI-generated image attached.' });
      onOpenChange(false);
    },
    [onImageGenerated, onOpenChange],
  );

  const handleFileSelect = useCallback(
    (file: UserFileResponse) => {
      if (!isMediaFile(file)) {
        toast({
          title: 'Not a media file',
          description: 'Only images, videos, and GIFs can be attached to posts.',
          variant: 'destructive',
        });
        return;
      }

      if (!isAcceptedMimeType(file.mimeType, acceptedMimeTypes)) {
        toast({
          title: 'Unsupported format',
          description: `This file type is not supported for the selected platform(s).`,
          variant: 'destructive',
        });
        return;
      }

      setSelectedFiles((prev) => {
        const alreadySelected = prev.some((f) => f.fileId === file.fileId);
        if (alreadySelected) {
          return prev.filter((f) => f.fileId !== file.fileId);
        }
        if (prev.length >= remainingSlots) {
          toast({
            title: 'Limit reached',
            description: `You can only add ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'}.`,
            variant: 'destructive',
          });
          return prev;
        }
        return [...prev, file];
      });
    },
    [acceptedMimeTypes, remainingSlots],
  );

  const handleConfirmSelection = useCallback(() => {
    if (selectedFiles.length === 0) return;
    onFilesSelected(selectedFiles);
    setSelectedFiles([]);
    onOpenChange(false);
  }, [selectedFiles, onFilesSelected, onOpenChange]);

  const handleUploadComplete = useCallback(
    (files: UserFileResponse[]) => {
      onUploadComplete(files);
      setUploadRefresh((n) => n + 1);
      toast({
        description: `${files.length} file${files.length === 1 ? '' : 's'} uploaded and attached.`,
      });
      onOpenChange(false);
    },
    [onUploadComplete, onOpenChange],
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setSelectedFiles([]);
      }
      onOpenChange(newOpen);
    },
    [onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Add Media
          </DialogTitle>
          <DialogDescription>
            Browse existing files, upload new media, or generate images with AI. You can add{' '}
            {remainingSlots} more file{remainingSlots === 1 ? '' : 's'}.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="browse" className="flex-1 flex flex-col min-h-0">
          <TabsList className="w-full bg-background rounded-2xl shadow-[var(--shadow-soft-blue)] p-1 gap-2">
            <TabsTrigger
              value="browse"
              className="
                font-normal
                text-[12px]
                flex items-center gap-1.5 flex-1 font-mono rounded-2xl
                data-[state=active]:bg-primary
                data-[state=active]:text-primary-foreground
                hover:bg-muted
              "
            >
              <FolderOpen className="h-auto w-4" />
              Browse Files
            </TabsTrigger>
            <TabsTrigger
              value="upload"
              className="
                font-normal
                text-[12px]
                flex items-center gap-1.5 flex-1 font-mono rounded-2xl
                data-[state=active]:bg-primary
                data-[state=active]:text-primary-foreground
                hover:bg-muted
              "
            >
              <Upload className="h-auto w-4" />
              Upload New
            </TabsTrigger>
            <TabsTrigger
              value="generate"
              className="
                font-normal
                text-[12px]
                flex items-center gap-1.5 flex-1 font-mono rounded-2xl
                data-[state=active]:bg-primary
                data-[state=active]:text-primary-foreground
                hover:bg-muted
              "
            >
              <Sparkles className="h-auto w-4" />
              Generate
            </TabsTrigger>
          </TabsList>

          <TabsContent value="browse" className="flex-1 flex flex-col min-h-0 mt-3 gap-3">
            <div className="flex-1 min-h-0 overflow-hidden border rounded-lg">
              <FileBrowser
                onFileSelect={handleFileSelect}
                multiSelect={false}
                status="active"
                className="h-full"
                refreshTrigger={uploadRefresh}
              />
            </div>

            {/* Selection bar */}
            {selectedFiles.length > 0 && (
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3 min-w-0 overflow-x-auto">
                  {selectedFiles.map((file) => (
                    <div
                      key={file.fileId}
                      className={cn(
                        'relative flex items-center gap-2 px-2 py-1 rounded-md bg-background border',
                        'text-sm flex-shrink-0',
                      )}
                    >
                      <Check className="h-3 w-3 text-primary" />
                      <span className="truncate max-w-[120px]">{file.fileName}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedFiles((prev) => prev.filter((f) => f.fileId !== file.fileId))
                        }
                        className="text-muted-foreground hover:text-foreground"
                        aria-label={`Deselect ${file.fileName}`}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
                <Button onClick={handleConfirmSelection} className="ml-3 flex-shrink-0">
                  Attach {selectedFiles.length} file{selectedFiles.length === 1 ? '' : 's'}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="upload" className="flex-1 min-h-0 mt-3">
            <FileUpload
              onUploadComplete={handleUploadComplete}
              accept={acceptedMimeTypes}
              maxFiles={remainingSlots}
              category="media"
              className="h-full"
            />
          </TabsContent>

          <TabsContent value="generate" className="flex-1 min-h-0 mt-3">
            <ImageGenerateTab onImageConfirmed={handleImageConfirmed} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
